import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
} from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { Controller } from 'react-hook-form';
import { schemaPanelStyles as styles } from '../styles/schemaPanelStyles';

// Enum option lists mirror the backend (GET /schemas validates these).
const PRIORITY_OPTIONS = ['P0', 'P1', 'P2', 'P3', 'P4'];
const EVENT_TYPE_OPTIONS = [
  { value: 'custom_event', label: 'Custom Event' },
  { value: 'screen_view', label: 'Screen View' },
  { value: 'commerce_event', label: 'Commerce Event' },
];
const EVENT_TYPE_LABELS = Object.fromEntries(EVENT_TYPE_OPTIONS.map((o) => [o.value, o.label]));

/**
 * Presentational schema filters — owns no state. Receives the react-hook-form
 * instance from useSchemaFilters and renders two multi-selects (priorities,
 * event types), two free-text searches (event name, event title), and Clear.
 */
export default function SchemaFilters({ form, onClear }) {
  const nameReg = form.register('searchEventName');
  const titleReg = form.register('searchEventTitle');

  return (
    <Box sx={styles.filters}>
      <Controller
        name="priorities"
        control={form.control}
        render={({ field }) => (
          <FormControl size="small" sx={styles.filterSelect}>
            <InputLabel id="schema-priorities-label">Priorities</InputLabel>
            <Select
              multiple
              labelId="schema-priorities-label"
              input={<OutlinedInput label="Priorities" />}
              renderValue={(selected) => (
                <Box sx={styles.chips}>
                  {selected.map((v) => (
                    <Chip key={v} label={v} size="small" />
                  ))}
                </Box>
              )}
              {...field}
            >
              {PRIORITY_OPTIONS.map((p) => (
                <MenuItem key={p} value={p}>
                  <Checkbox size="small" checked={field.value.includes(p)} />
                  <ListItemText primary={p} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      />

      <Controller
        name="eventTypes"
        control={form.control}
        render={({ field }) => (
          <FormControl size="small" sx={styles.filterSelect}>
            <InputLabel id="schema-event-types-label">Event types</InputLabel>
            <Select
              multiple
              labelId="schema-event-types-label"
              input={<OutlinedInput label="Event types" />}
              renderValue={(selected) => (
                <Box sx={styles.chips}>
                  {selected.map((v) => (
                    <Chip key={v} label={EVENT_TYPE_LABELS[v] ?? v} size="small" />
                  ))}
                </Box>
              )}
              {...field}
            >
              {EVENT_TYPE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  <Checkbox size="small" checked={field.value.includes(o.value)} />
                  <ListItemText primary={o.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      />

      <TextField
        fullWidth
        size="small"
        placeholder="Search event title"
        sx={styles.searchField}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={styles.searchIcon} />
            </InputAdornment>
          ),
        }}
        {...titleReg}
      />

      <TextField
        fullWidth
        size="small"
        placeholder="Search event name"
        sx={styles.searchField}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={styles.searchIcon} />
            </InputAdornment>
          ),
        }}
        {...nameReg}
      />

      <Button size="small" startIcon={<ClearIcon />} onClick={onClear} sx={styles.clearButton}>
        Clear
      </Button>
    </Box>
  );
}
